import * as React from "react"
import { useState, useEffect } from "react"
import { motion, AnimatePresence, type Variants } from "motion/react"
import {
  Trophy,
  Crown,
  Flame,
  Star,
  Sparkles,
  TrendingUp,
} from "lucide-react"
import { useAuth, type LeaderboardData, type LeaderboardEntry } from "@/contexts/AuthContext"
import coinImg from "@/public/coin.png"

const containerVariants: Variants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.08, delayChildren: 0.1 },
  },
}

const itemVariants: Variants = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { type: "spring", bounce: 0.3 } },
}

/* ── 3D Podium Block ──────────────────────────────────────────────── */

function PodiumBlock({
  bgColor,
  topColor,
  sideColor,
  height,
  label,
}: {
  bgColor: string
  topColor: string
  sideColor: string
  height: string
  label: string
}) {
  return (
    <div className={`w-full relative flex flex-col justify-end ${height}`}>
      {/* Isometric top face */}
      <svg
        className="w-full h-6 md:h-10 -mb-[2px] block relative z-10"
        preserveAspectRatio="none"
        viewBox="0 0 120 50"
      >
        <polygon
          points="15,0 105,0 120,50 0,50"
          fill={topColor}
          stroke="black"
          strokeWidth="4"
          vectorEffect="non-scaling-stroke"
          strokeLinejoin="round"
        />
      </svg>
      {/* Front face */}
      <div
        className="flex-1 w-full border-4 border-[#2A2A45] border-t-0 flex flex-col items-center justify-center relative z-20"
        style={{
          backgroundColor: bgColor,
          boxShadow: `inset -8px 0 0 ${sideColor}, 0 6px 0 #000`,
        }}
      >
        <span
          className="text-3xl md:text-5xl font-black text-white"
          style={{
            fontFamily: "var(--font-display)",
            textShadow:
              "3px 3px 0 #000, -1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000",
          }}
        >
          {label}
        </span>
      </div>
    </div>
  )
}

function LeaderboardAvatar({
  entry,
  mode,
  className,
  initialsClassName,
}: {
  entry: LeaderboardEntry
  mode: "agency" | "agent"
  className: string
  initialsClassName: string
}) {
  const [imageFailed, setImageFailed] = React.useState(false)
  const showImage = Boolean(entry.imageUrl) && !imageFailed
  const initial = (entry.name || (mode === "agency" ? "A" : "U")).charAt(0).toUpperCase()

  return (
    <div className={`${className} relative overflow-hidden`}>
      {showImage ? (
        <img
          src={entry.imageUrl!}
          alt={`${entry.name || "Leaderboard"} logo`}
          className="h-full w-full object-cover"
          loading="lazy"
          onError={() => setImageFailed(true)}
        />
      ) : (
        <span className={initialsClassName}>{initial}</span>
      )}
    </div>
  )
}

/* ── Podium Avatar ────────────────────────────────────────────────── */

function PodiumAvatar({
  rank,
  entry,
  mode,
}: {
  rank: number
  entry: LeaderboardEntry
  mode: "agency" | "agent"
}) {
  const isFirst = rank === 1

  const getCrown = () => {
    if (rank === 1)
      return (
        <Crown
          className="w-14 h-14 md:w-16 md:h-16 text-[#FFD700] fill-[#FFD700] drop-shadow-xl"
          strokeWidth={2}
        />
      )
    if (rank === 2)
      return (
        <Crown
          className="w-10 h-10 md:w-12 md:h-12 text-[#C0C0C0] fill-[#C0C0C0] drop-shadow-md"
          strokeWidth={2}
        />
      )
    return (
      <Crown
        className="w-10 h-10 md:w-12 md:h-12 text-[#CD7F32] fill-[#CD7F32] drop-shadow-md"
        strokeWidth={2}
      />
    )
  }

  return (
    <motion.div
      className={`flex flex-col items-center mb-2 relative z-30 ${isFirst ? "scale-110 -mt-6" : "scale-90"
        } translate-y-3`}
      animate={{ y: [0, -6, 0] }}
      transition={{ duration: 3 + rank * 0.5, repeat: Infinity, ease: "easeInOut" }}
    >
      {/* Crown */}
      <motion.div
        className="-mb-[16px] z-20 relative"
        animate={{ rotate: [-3, 3, -3], originY: 1 }}
        transition={{ duration: 2 + rank * 0.5, repeat: Infinity, ease: "easeInOut" }}
      >
        {getCrown()}
      </motion.div>

      {/* Avatar circle */}
      <LeaderboardAvatar
        entry={entry}
        mode={mode}
        className="w-14 h-14 md:w-16 md:h-16 rounded-xl border-4 border-[#2A2A45] shadow-[3px_3px_0_0_#000] bg-gradient-to-br from-gray-700 to-gray-900 relative z-0 flex items-center justify-center"
        initialsClassName="text-white font-black text-2xl drop-shadow-md"
      />

      {/* Deals badge */}
      <div className="-mt-3 z-10 bg-[#1C1C32] border-2 border-[#2A2A45] rounded px-2 py-0 shadow-[2px_2px_0_0_#000]">
        <span
          className="font-black text-[10px] md:text-xs text-black"
          style={{ fontFamily: "var(--font-body)" }}
        >
          {entry.deals}
        </span>
      </div>

      {/* Username */}
      <p
        className="font-bold text-white mt-1 text-xs md:text-sm drop-shadow-md z-10 truncate max-w-[100px] md:max-w-[120px] text-center"
        style={{
          textShadow:
            "1px 1px 0 #000, -1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000",
        }}
      >
        {entry.name || "Unknown"}
      </p>

      {/* Points pill */}
      <div className="bg-[#FFB84D] border-2 border-[#2A2A45] rounded-lg px-2 py-0.5 mt-0.5 flex items-center gap-1.5 shadow-[2px_2px_0_0_#000] z-10">
        <img
          src={coinImg}
          alt=""
          className="w-3.5 h-3.5 md:w-4 md:h-4 drop-shadow-[1px_1px_0_rgba(0,0,0,0.5)]"
        />
        <span
          className="font-black text-xs md:text-sm text-white"
          style={{
            fontFamily: "var(--font-body)",
            textShadow:
              "1px 1px 0 #000, -1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000",
          }}
        >
          {entry.earnings.toLocaleString()} pts
        </span>
      </div>

      {/* Current user badge */}
      {entry.isCurrentUser && (
        <motion.div
          className="mt-1 bg-[#F37020] border-2 border-[#2A2A45] rounded-full px-2 py-0 shadow-[2px_2px_0_0_#000] z-10"
          animate={{ scale: [1, 1.05, 1] }}
          transition={{ duration: 1.5, repeat: Infinity }}
        >
          <span
            className="font-black text-[8px] md:text-[10px] text-white uppercase tracking-wider"
            style={{ fontFamily: "var(--font-body)" }}
          >
            You
          </span>
        </motion.div>
      )}
    </motion.div>
  )
}

/* ── Podium Assembly ──────────────────────────────────────────────── */

function Podium({ top3, mode }: { top3: LeaderboardEntry[]; mode: "agency" | "agent" }) {
  if (top3.length === 0) return null

  return (
    <div className="flex items-end justify-center w-full max-w-2xl mx-auto mt-10 md:mt-14 mb-0 px-2 md:px-4">
      {/* 3RD PLACE */}
      <motion.div
        className="flex flex-col items-center flex-1 z-10"
        variants={itemVariants}
        whileHover={{ scale: 1.02, y: -4 }}
      >
        {top3[2] && <PodiumAvatar rank={3} entry={top3[2]} mode={mode} />}
        <PodiumBlock
          bgColor="#CD7F32"
          topColor="#D4915A"
          sideColor="#A56429"
          height="h-28 md:h-40"
          label="3RD"
        />
      </motion.div>

      {/* 1ST PLACE */}
      <motion.div
        className="flex flex-col items-center flex-1 z-20 -mx-3 md:-mx-5"
        variants={itemVariants}
        whileHover={{ scale: 1.02, y: -4 }}
      >
        {top3[0] && <PodiumAvatar rank={1} entry={top3[0]} mode={mode} />}
        <PodiumBlock
          bgColor="#FFB84D"
          topColor="#FFCC66"
          sideColor="#E6A040"
          height="h-40 md:h-60"
          label="1ST"
        />
      </motion.div>

      {/* 2ND PLACE */}
      <motion.div
        className="flex flex-col items-center flex-1 z-10"
        variants={itemVariants}
        whileHover={{ scale: 1.02, y: -4 }}
      >
        {top3[1] && <PodiumAvatar rank={2} entry={top3[1]} mode={mode} />}
        <PodiumBlock
          bgColor="#C0C0C0"
          topColor="#D4D4D4"
          sideColor="#A0A0A0"
          height="h-32 md:h-48"
          label="2ND"
        />
      </motion.div>
    </div>
  )
}

/* ── Table Row ────────────────────────────────────────────────────── */

function TableRow({
  entry,
  index,
  mode,
}: {
  entry: LeaderboardEntry
  index: number
  mode: "agency" | "agent"
}) {
  return (
    <motion.div
      className={`grid grid-cols-[auto_1fr_auto_auto] md:grid-cols-[60px_1fr_120px_100px_120px] p-3 md:p-4 items-center gap-3 md:gap-4 hover:bg-[#1C1C32]/20 transition-colors cursor-pointer ${entry.isCurrentUser ? "bg-[#F37020]/10 border-l-4 border-l-[#F37020]" : ""
        }`}
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      whileHover={{ scale: 1.01, x: 4, backgroundColor: 'rgba(255, 255, 255, 0.1)' }}
      whileTap={{ scale: 0.99 }}
      transition={{ delay: index * 0.04 }}
    >
      {/* Rank */}
      <div className="flex items-center gap-2">
        {index < 3 ? (
          <TrendingUp className="w-3.5 h-3.5 text-[#32CD32] drop-shadow-[0_1px_0_rgba(0,0,0,1)]" />
        ) : (
          <div className="w-3.5" />
        )}
        <span
          className="font-black text-white text-sm md:text-base drop-shadow-[1px_1px_0_rgba(0,0,0,1)]"
          style={{ fontFamily: "var(--font-body)" }}
        >
          {String(entry.rank).padStart(2, "0")}
        </span>
      </div>

      {/* Name */}
      <div className="flex items-center gap-2.5 min-w-0">
        <LeaderboardAvatar
          entry={entry}
          mode={mode}
          className="w-7 h-7 md:w-8 md:h-8 shrink-0 rounded-full bg-gradient-to-br from-gray-700 to-gray-900 border-2 border-[#2A2A45] flex items-center justify-center"
          initialsClassName="text-white font-black text-xs"
        />
        <div className="min-w-0 flex items-center gap-2">
          <span
            className="font-bold text-white text-xs md:text-sm truncate drop-shadow-[1px_1px_0_rgba(0,0,0,1)]"
            style={{ fontFamily: "var(--font-body)" }}
          >
            {entry.name || "Unknown"}
          </span>
          {entry.isCurrentUser && (
            <span className="shrink-0 text-[8px] md:text-[10px] bg-[#F37020] text-white px-1.5 py-0.5 rounded font-black border border-[#2A2A45] shadow-[1px_1px_0_0_#000]">
              YOU
            </span>
          )}
        </div>
      </div>

      {/* Deals — hidden on small screens */}
      <div className="hidden md:flex items-center gap-2">
        <div className="w-6 h-6 shrink-0 rounded-full bg-[#9B59B6] border-2 border-[#2A2A45] flex items-center justify-center shadow-[1px_1px_0_0_#000]">
          <Star className="w-3 h-3 text-white fill-white" />
        </div>
        <span
          className="font-bold text-white/90 text-xs md:text-sm drop-shadow-[1px_1px_0_rgba(0,0,0,1)]"
          style={{ fontFamily: "var(--font-body)" }}
        >
          {entry.deals} {mode === "agent" ? "deals" : "deals"}
        </span>
      </div>

      {/* Rank badge — hidden on small screens */}
      <div className="hidden md:flex items-center gap-1.5">
        <Trophy className="w-4 h-4 text-[#FFD700] drop-shadow-[1px_1px_0_rgba(0,0,0,1)]" />
        <span
          className="font-bold text-white text-xs md:text-sm drop-shadow-[1px_1px_0_rgba(0,0,0,1)]"
          style={{ fontFamily: "var(--font-body)" }}
        >
          #{entry.rank}
        </span>
      </div>

      {/* Points */}
      <div className="flex items-center gap-1.5 justify-end">
        <img
          src={coinImg}
          alt=""
          className="w-4 h-4 md:w-5 md:h-5 drop-shadow-[1px_1px_0_rgba(0,0,0,1)]"
        />
        <span
          className="font-black text-white text-xs md:text-sm drop-shadow-[1px_1px_0_rgba(0,0,0,1)]"
          style={{ fontFamily: "var(--font-body)" }}
        >
          {entry.earnings.toLocaleString()} pts
        </span>
      </div>
    </motion.div>
  )
}

/* ── Main LeaderboardTab ──────────────────────────────────────────── */

export function LeaderboardTab() {
  const { getLeaderboard } = useAuth()
  const [data, setData] = useState<LeaderboardData | null>(null)
  const [loading, setLoading] = useState(true)
  const [mode, setMode] = useState<"agency" | "agent">("agent")

  useEffect(() => {
    setLoading(true)
    getLeaderboard(mode).then((result) => {
      if (result.success && result.data) {
        setData(result.data)
      }
      setLoading(false)
    })
  }, [getLeaderboard, mode])

  const top3 = data?.leaderboard.slice(0, 3) || []
  const rest = data?.leaderboard.slice(3) || []

  return (
    <div
      className="min-h-screen w-full relative pt-6 pb-32 px-3 md:px-4"
      style={{
        background:
          "repeating-conic-gradient(from 0deg, #3E100C 0deg 10deg, #2D0B08 10deg 20deg)",
        boxShadow: "inset 0 0 120px rgba(0,0,0,0.6)",
      }}
    >
      {/* Subtle radial overlay */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse at 50% 20%, rgba(255,184,77,0.08) 0%, transparent 60%)",
        }}
      />

      <motion.div
        className="max-w-4xl mx-auto relative z-10"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        {/* Header */}
        <motion.div className="flex flex-col items-center mb-6 md:mb-10" variants={itemVariants}>
          <div className="flex items-center gap-3 mb-4">
            <motion.div
              animate={{ rotate: [0, 360], scale: [1, 1.1, 1] }}
              transition={{ rotate: { duration: 4, repeat: Infinity, ease: "linear" }, scale: { duration: 2, repeat: Infinity, ease: "easeInOut" } }}
            >
              <img src={coinImg} alt="" className="w-8 h-8 md:w-10 md:h-10 drop-shadow-[0_0_8px_rgba(255,215,0,0.5)]" />
            </motion.div>
            <h1
              className="text-4xl md:text-6xl text-[#FFD700] tracking-wider"
              style={{
                fontFamily: "var(--font-display)",
                textShadow:
                  "4px 4px 0 #000, -2px -2px 0 #000, 2px -2px 0 #000, -2px 2px 0 #000, 0 0 20px rgba(255,215,0,0.3)",
              }}
            >
              LEADERBOARD
            </h1>
            <motion.div
              animate={{ rotate: [360, 0], scale: [1, 1.1, 1] }}
              transition={{ rotate: { duration: 4.5, repeat: Infinity, ease: "linear" }, scale: { duration: 2.2, repeat: Infinity, ease: "easeInOut" } }}
            >
              <img src={coinImg} alt="" className="w-8 h-8 md:w-10 md:h-10 drop-shadow-[0_0_8px_rgba(255,215,0,0.5)]" />
            </motion.div>
          </div>
        </motion.div>

        {/* Toggle Switch */}
        <motion.div className="flex justify-center mb-6 md:mb-10" variants={itemVariants}>
          <div className="bg-[#1A0B07] border-4 border-[#2A2A45] p-1.5 rounded-full flex gap-1 shadow-[4px_4px_0_0_#000]">
            <motion.button
              whileTap={{ scale: 0.95 }}
              onClick={() => setMode("agent")}
              className={`px-6 md:px-10 py-2.5 rounded-full font-black text-sm md:text-base border-2 transition-all cursor-pointer ${mode === "agent"
                ? "bg-[#F37020] text-black border-[#2A2A45] shadow-[inset_0_-3px_0_rgba(0,0,0,0.3)]"
                : "bg-transparent text-white/50 border-transparent hover:text-white"
                }`}
              style={{ fontFamily: "var(--font-body)" }}
            >
              Agents
            </motion.button>
            <motion.button
              whileTap={{ scale: 0.95 }}
              onClick={() => setMode("agency")}
              className={`px-6 md:px-10 py-2.5 rounded-full font-black text-sm md:text-base border-2 transition-all cursor-pointer ${mode === "agency"
                ? "bg-[#F37020] text-black border-[#2A2A45] shadow-[inset_0_-3px_0_rgba(0,0,0,0.3)]"
                : "bg-transparent text-white/50 border-transparent hover:text-white"
                }`}
              style={{ fontFamily: "var(--font-body)" }}
            >
              Agencies
            </motion.button>
          </div>
        </motion.div>

        {loading ? (
          <div className="flex flex-col items-center justify-center py-32 gap-4">
            <motion.img
              src={coinImg}
              alt=""
              className="w-16 h-16 drop-shadow-xl"
              animate={{ rotate: [0, 360], scale: [1, 1.1, 1] }}
              transition={{
                rotate: { duration: 1.5, repeat: Infinity, ease: "linear" },
                scale: { duration: 0.8, repeat: Infinity },
              }}
            />
          </div>
        ) : data && data.leaderboard.length > 0 ? (
          <AnimatePresence mode="wait">
            <motion.div
              key={mode}
              variants={containerVariants}
              initial="hidden"
              animate="visible"
              exit="hidden"
            >
              {/* Podium */}
            <Podium top3={top3} mode={mode} />

              {/* Table of remaining entries */}
              {rest.length > 0 && (
                <motion.div
                  variants={itemVariants}
                  className="bg-[#30140D] border-4 border-[#2A2A45] rounded-[16px] shadow-[8px_8px_0_0_#000] mt-8 relative z-30 overflow-hidden"
                >
                  {/* Table header */}
                  <div className="grid grid-cols-[auto_1fr_auto_auto] md:grid-cols-[60px_1fr_120px_100px_120px] p-3 md:p-4 bg-[#F37020] border-b-4 border-[#2A2A45] items-center gap-3 md:gap-4">
                    <div
                      className="font-black text-black text-[10px] md:text-xs uppercase tracking-wider"
                      style={{ fontFamily: "var(--font-body)" }}
                    >
                      Place
                    </div>
                    <div
                      className="font-black text-black text-[10px] md:text-xs uppercase tracking-wider"
                      style={{ fontFamily: "var(--font-body)" }}
                    >
                      {mode === "agent" ? "Agent" : "Agency"}
                    </div>
                    <div
                      className="hidden md:block font-black text-black text-[10px] md:text-xs uppercase tracking-wider"
                      style={{ fontFamily: "var(--font-body)" }}
                    >
                      Deals
                    </div>
                    <div
                      className="hidden md:block font-black text-black text-[10px] md:text-xs uppercase tracking-wider"
                      style={{ fontFamily: "var(--font-body)" }}
                    >
                      Rank
                    </div>
                    <div
                      className="font-black text-black text-[10px] md:text-xs uppercase tracking-wider text-right"
                      style={{ fontFamily: "var(--font-body)" }}
                    >
                      Points
                    </div>
                  </div>

                  {/* Table rows */}
                  <div className="divide-y-2 divide-black/30">
                    {rest.map((entry, idx) => (
                      <TableRow key={entry.name + entry.rank} entry={entry} index={idx} mode={mode} />
                    ))}
                  </div>
                </motion.div>
              )}

              {/* Current user position (if not in top entries) */}
              {data?.currentUserPosition &&
                !data.leaderboard.some((e) => e.isCurrentUser) && (
                  <motion.div variants={itemVariants} className="mt-8 flex justify-center w-full">
                    <div className="bg-[#1A0B07] border-4 border-[#F37020] rounded-xl p-4 shadow-[6px_6px_0_0_#F37020] flex items-center gap-4 w-full md:w-auto relative z-30">
                      <Flame className="w-6 h-6 text-[#F37020]" />
                      <div>
                        <p
                          className="text-[#F37020] font-black text-sm uppercase tracking-wider"
                          style={{ fontFamily: "var(--font-body)" }}
                        >
                          Your Position: #{data.currentUserPosition.rank}
                        </p>
                        <p
                          className="text-white font-bold"
                          style={{ fontFamily: "var(--font-body)" }}
                        >
                          {data.currentUserPosition.earnings.toLocaleString()} pts
                          &middot; {data.currentUserPosition.deals} deals
                        </p>
                      </div>
                    </div>
                  </motion.div>
                )}
            </motion.div>
          </AnimatePresence>
        ) : (
          <motion.div
            variants={itemVariants}
            initial="hidden"
            animate="visible"
            className="flex flex-col items-center justify-center py-24 bg-[#30140D] border-4 border-[#2A2A45] rounded-[16px] shadow-[8px_8px_0_0_#000] mt-6 relative z-30 overflow-hidden"
          >
            {/* Subtle background pattern for empty state */}
            <div className="absolute inset-0 opacity-10" style={{ background: "repeating-linear-gradient(45deg, transparent, transparent 10px, #000 10px, #000 20px)" }} />

            <motion.div
              animate={{ rotate: [-5, 5, -5], y: [0, -10, 0] }}
              transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
              className="relative z-10"
            >
              <Trophy className="w-20 h-20 text-[#5C2B1D] drop-shadow-lg mb-6" />
            </motion.div>

            <p
              className="text-2xl md:text-3xl font-black text-white mb-2 drop-shadow-[2px_2px_0_rgba(0,0,0,1)] text-center px-4 relative z-10 uppercase tracking-wide"
              style={{ fontFamily: "var(--font-display)" }}
            >
              {mode === "agency" ? "No Agencies Ranked Yet" : "No Agents Ranked Yet"}
            </p>
            <p
              className="text-white/60 text-sm md:text-base font-bold text-center px-4 max-w-md relative z-10"
              style={{ fontFamily: "var(--font-body)" }}
            >
              {mode === "agency"
                ? "The leaderboard is waiting for the first agency to close a deal. Will it be yours?"
                : "The leaderboard is waiting for the first agent to close a deal. Time to get to work!"}
            </p>

            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="mt-8 px-8 py-3 bg-[#F37020] text-black font-black uppercase tracking-wider text-sm border-2 border-[#2A2A45] rounded-full shadow-[4px_4px_0_0_#000] hover:shadow-[2px_2px_0_0_#000] hover:translate-y-[2px] transition-all relative z-10 cursor-pointer"
              style={{ fontFamily: "var(--font-body)" }}
            >
              Start Closing Deals
            </motion.button>
          </motion.div>
        )}
      </motion.div>
    </div>
  )
}
